import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMarketOverview, useMarketSegments, usePortfolioPosition, useMarketChannels } from '@/hooks/useMarket';
import { LoadingPage, LoadingCard } from '@/components/ui/LoadingSpinner';
import { ErrorPage } from '@/components/ui/ErrorMessage';
import { MetricTooltip } from '@/components/ui/Tooltip';
import { SectionHeaderWithFreshness, PROVENANCE_PRESETS, SimpleLineChart, type CompetitorData } from '@/components/ui';

// Mock SKU data for competitors (in production, this would come from API)
const COMPETITOR_SKUS: Record<string, Array<{
  gtin: string;
  name: string;
  size: string;
  revenue: number;
  revenueShare: number;
  growth: number;
}>> = {
  'Coca-Cola': [
    { gtin: '7501055300044', name: 'Coca-Cola Original 600ml', size: '600ml', revenue: 245000000, revenueShare: 0.32, growth: 0.02 },
    { gtin: '7501055300051', name: 'Coca-Cola Original 2L', size: '2L', revenue: 189000000, revenueShare: 0.25, growth: -0.01 },
    { gtin: '7501055300068', name: 'Coca-Cola Original 355ml Can', size: '355ml', revenue: 156000000, revenueShare: 0.20, growth: 0.05 },
    { gtin: '7501055300075', name: 'Coca-Cola Zero 600ml', size: '600ml', revenue: 98000000, revenueShare: 0.13, growth: 0.12 },
    { gtin: '7501055300082', name: 'Coca-Cola Original 1.5L', size: '1.5L', revenue: 78000000, revenueShare: 0.10, growth: 0.03 },
  ],
  'Pepsi': [
    { gtin: '7501055400044', name: 'Pepsi Original 600ml', size: '600ml', revenue: 98000000, revenueShare: 0.35, growth: 0.03 },
    { gtin: '7501055400051', name: 'Pepsi Original 2L', size: '2L', revenue: 72000000, revenueShare: 0.26, growth: 0.01 },
    { gtin: '7501055400068', name: 'Pepsi Black 600ml', size: '600ml', revenue: 45000000, revenueShare: 0.16, growth: 0.15 },
    { gtin: '7501055400075', name: 'Pepsi Original 355ml Can', size: '355ml', revenue: 38000000, revenueShare: 0.14, growth: 0.02 },
    { gtin: '7501055400082', name: 'Pepsi Original 1.5L', size: '1.5L', revenue: 25000000, revenueShare: 0.09, growth: -0.02 },
  ],
  'Red Cola': [
    { gtin: '7501055500044', name: 'Red Cola Original 600ml', size: '600ml', revenue: 42000000, revenueShare: 0.38, growth: 0.08 },
    { gtin: '7501055500051', name: 'Red Cola Original 2L', size: '2L', revenue: 28000000, revenueShare: 0.25, growth: 0.05 },
    { gtin: '7501055500068', name: 'Red Cola Original 355ml', size: '355ml', revenue: 22000000, revenueShare: 0.20, growth: 0.12 },
    { gtin: '7501055500075', name: 'Red Cola Zero 600ml', size: '600ml', revenue: 11000000, revenueShare: 0.10, growth: 0.25 },
    { gtin: '7501055500082', name: 'Red Cola Original 1L', size: '1L', revenue: 8000000, revenueShare: 0.07, growth: 0.03 },
  ],
  'Big Cola': [
    { gtin: '7501055600044', name: 'Big Cola Original 3L', size: '3L', revenue: 35000000, revenueShare: 0.40, growth: 0.06 },
    { gtin: '7501055600051', name: 'Big Cola Original 2L', size: '2L', revenue: 25000000, revenueShare: 0.29, growth: 0.04 },
    { gtin: '7501055600068', name: 'Big Cola Original 600ml', size: '600ml', revenue: 15000000, revenueShare: 0.17, growth: 0.10 },
    { gtin: '7501055600075', name: 'Big Cola Sabor Limón 2L', size: '2L', revenue: 8000000, revenueShare: 0.09, growth: 0.15 },
    { gtin: '7501055600082', name: 'Big Cola Original 1.5L', size: '1.5L', revenue: 4000000, revenueShare: 0.05, growth: 0.02 },
  ],
};

// Generate generic SKUs for brands without specific data
function getCompetitorSKUs(brandName: string) {
  if (COMPETITOR_SKUS[brandName]) {
    return COMPETITOR_SKUS[brandName];
  }
  // Generate generic SKUs
  return [
    { gtin: `${brandName}-001`, name: `${brandName} Original 600ml`, size: '600ml', revenue: 50000000, revenueShare: 0.35, growth: 0.03 },
    { gtin: `${brandName}-002`, name: `${brandName} Original 2L`, size: '2L', revenue: 35000000, revenueShare: 0.24, growth: 0.01 },
    { gtin: `${brandName}-003`, name: `${brandName} Light 600ml`, size: '600ml', revenue: 28000000, revenueShare: 0.19, growth: 0.08 },
    { gtin: `${brandName}-004`, name: `${brandName} Original 355ml`, size: '355ml', revenue: 20000000, revenueShare: 0.14, growth: 0.05 },
    { gtin: `${brandName}-005`, name: `${brandName} Zero 600ml`, size: '600ml', revenue: 12000000, revenueShare: 0.08, growth: 0.12 },
  ];
}

// Attribute/Level preferences for each segment type (CPG conjoint-style)
const SEGMENT_ATTRIBUTE_PREFERENCES: Record<string, Array<{
  attribute: string;
  attribute_es: string;
  levels: Array<{ level: string; level_es: string; preference: number }>;
}>> = {
  premium: [
    { attribute: 'Quality', attribute_es: 'Calidad', levels: [
      { level: 'Premium', level_es: 'Premium', preference: 0.65 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.25 },
      { level: 'Basic', level_es: 'Básico', preference: 0.10 },
    ]},
    { attribute: 'Pack Size', attribute_es: 'Tamaño', levels: [
      { level: 'Single Serve', level_es: 'Individual', preference: 0.40 },
      { level: 'Multi-pack', level_es: 'Multi-pack', preference: 0.35 },
      { level: 'Family Size', level_es: 'Familiar', preference: 0.25 },
    ]},
  ],
  family: [
    { attribute: 'Pack Size', attribute_es: 'Tamaño', levels: [
      { level: 'Family Size', level_es: 'Familiar', preference: 0.55 },
      { level: 'Multi-pack', level_es: 'Multi-pack', preference: 0.30 },
      { level: 'Single Serve', level_es: 'Individual', preference: 0.15 },
    ]},
    { attribute: 'Price Tier', attribute_es: 'Precio', levels: [
      { level: 'Mid-Range', level_es: 'Medio', preference: 0.55 },
      { level: 'Value', level_es: 'Económico', preference: 0.30 },
      { level: 'Premium', level_es: 'Premium', preference: 0.15 },
    ]},
  ],
  kids: [
    { attribute: 'Packaging', attribute_es: 'Empaque', levels: [
      { level: 'Fun/Character', level_es: 'Divertido', preference: 0.60 },
      { level: 'Colorful', level_es: 'Colorido', preference: 0.30 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.10 },
    ]},
    { attribute: 'Flavor', attribute_es: 'Sabor', levels: [
      { level: 'Chocolate', level_es: 'Chocolate', preference: 0.45 },
      { level: 'Strawberry', level_es: 'Fresa', preference: 0.35 },
      { level: 'Vanilla', level_es: 'Vainilla', preference: 0.20 },
    ]},
  ],
  value: [
    { attribute: 'Price Tier', attribute_es: 'Precio', levels: [
      { level: 'Value/Economy', level_es: 'Económico', preference: 0.60 },
      { level: 'Mid-Range', level_es: 'Medio', preference: 0.30 },
      { level: 'Premium', level_es: 'Premium', preference: 0.10 },
    ]},
    { attribute: 'Pack Size', attribute_es: 'Tamaño', levels: [
      { level: 'Large/Bulk', level_es: 'Grande/Bulk', preference: 0.50 },
      { level: 'Family Size', level_es: 'Familiar', preference: 0.35 },
      { level: 'Single Serve', level_es: 'Individual', preference: 0.15 },
    ]},
  ],
  health: [
    { attribute: 'Ingredients', attribute_es: 'Ingredientes', levels: [
      { level: 'Organic/Natural', level_es: 'Orgánico/Natural', preference: 0.55 },
      { level: 'Clean Label', level_es: 'Etiqueta limpia', preference: 0.30 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.15 },
    ]},
    { attribute: 'Sugar Content', attribute_es: 'Azúcar', levels: [
      { level: 'No Sugar', level_es: 'Sin azúcar', preference: 0.45 },
      { level: 'Low Sugar', level_es: 'Bajo en azúcar', preference: 0.40 },
      { level: 'Regular', level_es: 'Regular', preference: 0.15 },
    ]},
  ],
  convenience: [
    { attribute: 'Format', attribute_es: 'Formato', levels: [
      { level: 'Ready-to-Eat', level_es: 'Listo para comer', preference: 0.55 },
      { level: 'Quick Prep', level_es: 'Rápida preparación', preference: 0.30 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.15 },
    ]},
    { attribute: 'Packaging', attribute_es: 'Empaque', levels: [
      { level: 'Resealable', level_es: 'Resellable', preference: 0.45 },
      { level: 'Portable', level_es: 'Portátil', preference: 0.40 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.15 },
    ]},
  ],
  indulgence: [
    { attribute: 'Flavor Intensity', attribute_es: 'Intensidad', levels: [
      { level: 'Rich/Intense', level_es: 'Rico/Intenso', preference: 0.55 },
      { level: 'Balanced', level_es: 'Balanceado', preference: 0.30 },
      { level: 'Light', level_es: 'Ligero', preference: 0.15 },
    ]},
    { attribute: 'Quality Tier', attribute_es: 'Calidad', levels: [
      { level: 'Artisan/Craft', level_es: 'Artesanal', preference: 0.50 },
      { level: 'Premium', level_es: 'Premium', preference: 0.35 },
      { level: 'Standard', level_es: 'Estándar', preference: 0.15 },
    ]},
  ],
  traditional: [
    { attribute: 'Brand Heritage', attribute_es: 'Tradición', levels: [
      { level: 'Heritage Brand', level_es: 'Marca tradicional', preference: 0.55 },
      { level: 'Established', level_es: 'Establecida', preference: 0.30 },
      { level: 'New Brand', level_es: 'Nueva', preference: 0.15 },
    ]},
    { attribute: 'Flavor', attribute_es: 'Sabor', levels: [
      { level: 'Classic', level_es: 'Clásico', preference: 0.60 },
      { level: 'Traditional', level_es: 'Tradicional', preference: 0.30 },
      { level: 'Modern', level_es: 'Moderno', preference: 0.10 },
    ]},
  ],
};

function getAttributePreferencesForSegment(segmentName: string) {
  const name = segmentName.toLowerCase();
  if (name.includes('family') || name.includes('familia')) return SEGMENT_ATTRIBUTE_PREFERENCES.family;
  if (name.includes('kid') || name.includes('niño') || name.includes('children')) return SEGMENT_ATTRIBUTE_PREFERENCES.kids;
  if (name.includes('indulgen') || name.includes('treat')) return SEGMENT_ATTRIBUTE_PREFERENCES.indulgence;
  if (name.includes('premium') || name.includes('gourmet') || name.includes('luxury')) return SEGMENT_ATTRIBUTE_PREFERENCES.premium;
  if (name.includes('value') || name.includes('price') || name.includes('budget') || name.includes('económic')) return SEGMENT_ATTRIBUTE_PREFERENCES.value;
  if (name.includes('health') || name.includes('organic') || name.includes('natural') || name.includes('salud')) return SEGMENT_ATTRIBUTE_PREFERENCES.health;
  if (name.includes('convenience') || name.includes('busy') || name.includes('quick') || name.includes('rápid')) return SEGMENT_ATTRIBUTE_PREFERENCES.convenience;
  return SEGMENT_ATTRIBUTE_PREFERENCES.traditional;
}

// Expandable segment row component with attribute preferences
function SegmentRow({
  segment,
  isLast
}: {
  segment: {
    id: string;
    name: string;
    share: number;
    price_sensitivity?: number;
    characteristics?: string[]
  };
  isLast: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const attributePreferences = getAttributePreferencesForSegment(segment.name);

  return (
    <>
      <tr
        className={`cursor-pointer hover:bg-surface-600/30 transition-colors ${!isLast && !isExpanded ? 'border-b border-surface-400/20' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <td className="py-3 text-white font-medium">
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-text-tertiary transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {segment.name}
          </div>
        </td>
        <td className="py-3 text-right text-text-secondary">{(segment.share * 100).toFixed(1)}%</td>
        <td className="py-3 text-right text-text-secondary">
          {segment.price_sensitivity?.toFixed(1) || '-'}
        </td>
        <td className="py-3 text-text-secondary pl-6">
          {segment.characteristics?.slice(0, 3).join(', ') || '-'}
        </td>
      </tr>
      {isExpanded && (
        <tr className={!isLast ? 'border-b border-surface-400/20' : ''}>
          <td colSpan={4} className="py-4 px-4 bg-surface-700/30">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <h4 className="text-sm font-medium text-text-secondary">Product Preferences</h4>
                <MetricTooltip metric="preferenceShare" position="right" />
              </div>
              <span className="text-xs text-text-tertiary flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                From conjoint surveys
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {attributePreferences.map((attr, attrIdx) => (
                <div key={attrIdx} className="bg-surface-600/50 rounded-lg p-3">
                  <div className="text-xs font-medium text-text-secondary mb-2">{attr.attribute}</div>
                  <div className="space-y-1">
                    {attr.levels.map((level, levelIdx) => {
                      const barColor = level.preference >= 0.5
                        ? 'bg-primary-500'
                        : level.preference >= 0.3
                          ? 'bg-primary-500/70'
                          : 'bg-primary-500/40';
                      return (
                        <div key={levelIdx} className="flex items-center gap-2">
                          <div className="w-20 text-xs text-text-tertiary truncate">{level.level}</div>
                          <div className="flex-1 h-3 bg-surface-500 rounded overflow-hidden">
                            <div
                              className={`h-full ${barColor} rounded transition-all duration-300`}
                              style={{ width: `${level.preference * 100}%` }}
                            />
                          </div>
                          <div className="w-8 text-xs text-text-tertiary text-right" title="Preference % of consumers">
                            {(level.preference * 100).toFixed(0)}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Subchannel data for each channel type
const CHANNEL_SUBCHANNELS: Record<string, Array<{
  id: string;
  name: string;
  share: number;
  examples: string[];
}>> = {
  modern_trade: [
    { id: 'hypermarket', name: 'Hypermarkets', share: 0.18, examples: ['Walmart', 'Éxito', 'Jumbo'] },
    { id: 'supermarket', name: 'Supermarkets', share: 0.20, examples: ['Carulla', 'Olímpica', 'Metro'] },
    { id: 'discounter', name: 'Discounters', share: 0.12, examples: ['D1', 'Ara', 'Justo & Bueno'] },
  ],
  traditional: [
    { id: 'tienda', name: 'Neighborhood Stores', share: 0.25, examples: ['Tiendas', 'Minimercados'] },
    { id: 'market', name: 'Open Markets', share: 0.08, examples: ['Plazas de mercado'] },
  ],
  convenience: [
    { id: 'cstore', name: 'C-Stores', share: 0.10, examples: ['OXXO', 'Circle K'] },
    { id: 'gas_station', name: 'Gas Stations', share: 0.05, examples: ['Terpel', 'Primax'] },
  ],
  ecommerce: [
    { id: 'marketplace', name: 'Marketplaces', share: 0.04, examples: ['Rappi', 'Merqueo'] },
    { id: 'dstore', name: 'Direct Store', share: 0.02, examples: ['Brand websites'] },
  ],
};

function getSubchannelsForChannel(channelId: string) {
  return CHANNEL_SUBCHANNELS[channelId] || CHANNEL_SUBCHANNELS['traditional'];
}

// Expandable channel row component with subchannel drill-down
function ChannelRow({
  channel,
  isLast
}: {
  channel: {
    id: string;
    name: string;
    share: number;
    growth: number;
    description?: string;
    characteristics?: string[];
  };
  isLast: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const subchannels = getSubchannelsForChannel(channel.id);

  const growthColor = channel.growth >= 0 ? 'text-success-400' : 'text-danger-400';
  const growthSign = channel.growth >= 0 ? '+' : '';

  return (
    <>
      <tr
        className={`cursor-pointer hover:bg-surface-600/30 transition-colors ${!isLast && !isExpanded ? 'border-b border-surface-400/20' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <td className="py-3 text-white font-medium">
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-text-tertiary transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {channel.name}
          </div>
        </td>
        <td className="py-3 text-right text-text-secondary">{(channel.share * 100).toFixed(1)}%</td>
        <td className={`py-3 text-right ${growthColor}`}>
          {growthSign}{(channel.growth * 100).toFixed(1)}%
        </td>
        <td className="py-3 text-text-secondary pl-6">
          {channel.description || '-'}
        </td>
      </tr>
      {isExpanded && (
        <tr className={!isLast ? 'border-b border-surface-400/20' : ''}>
          <td colSpan={4} className="py-4 px-4 bg-surface-700/30">
            {/* Subchannels */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-text-secondary">Subchannels</h4>
                <span className="text-xs text-text-tertiary">Share of category volume</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {subchannels.map((sub) => (
                  <div key={sub.id} className="bg-surface-600/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white">{sub.name}</span>
                      <span className="text-sm text-primary-400" title="Volume share of category">
                        {(sub.share * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-surface-500 rounded overflow-hidden mb-2">
                      <div
                        className="h-full bg-primary-500/70 rounded"
                        style={{ width: `${(sub.share / channel.share) * 100}%` }}
                      />
                    </div>
                    <div className="text-xs text-text-tertiary">
                      {sub.examples.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Channel Characteristics */}
            {channel.characteristics && channel.characteristics.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">Channel Characteristics</h4>
                <div className="flex flex-wrap gap-2">
                  {channel.characteristics.map((char, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-surface-600 text-text-secondary rounded">
                      {char}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function formatCurrency(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatVolume(value: number): string {
  if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B units`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(0)}M units`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K units`;
  return `${value.toFixed(0)} units`;
}

function formatPercent(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(1)}%`;
}

// Competition section sub-tabs
type CompetitionTab = 'rankings' | 'dynamics' | 'deep-dive';

export function MarketPage() {
  const { clientId } = useParams();
  const overviewQuery = useMarketOverview(clientId);
  const segmentsQuery = useMarketSegments(clientId);
  const positionQuery = usePortfolioPosition(clientId);
  const channelsQuery = useMarketChannels(clientId);

  // Competition section state
  const [competitionTab, setCompetitionTab] = useState<CompetitionTab>('rankings');
  const [selectedCompetitorName, setSelectedCompetitorName] = useState<string | null>(null);

  if (overviewQuery.isLoading) {
    return <LoadingPage message="Loading market data..." />;
  }

  if (overviewQuery.error) {
    return <ErrorPage error={overviewQuery.error} onRetry={() => overviewQuery.refetch()} />;
  }

  const overview = overviewQuery.data;
  const segments = segmentsQuery.data;
  const position = positionQuery.data;

  // Get selected competitor data
  const selectedCompetitor = selectedCompetitorName
    ? overview?.top_brands?.find(b => b.name === selectedCompetitorName)
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-white mb-6">Market Analysis</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Market Overview */}
        <div className="card p-6">
          <SectionHeaderWithFreshness
            title="Market Overview"
            provenance={PROVENANCE_PRESETS.nielsenRecent(new Date('2023-11-15'))}
          />
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-text-secondary">Total Market Value</span>
              <span className="text-white font-medium">
                {overview ? formatCurrency(overview.market.total_value) : '-'}
                <span className="text-xs text-text-tertiary ml-1">(Revenue)</span>
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-secondary">Total Volume</span>
              <span className="text-white font-medium">
                {overview ? formatVolume(overview.market.total_volume) : '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1">
                <span className="text-text-secondary">Growth Rate</span>
                <MetricTooltip metric="growthRate" position="right" />
              </div>
              <span className={`font-medium ${overview && overview.market.growth_rate >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                {overview ? formatPercent(overview.market.growth_rate) : '-'}
                <span className="text-xs text-text-tertiary ml-1">(Vol YoY)</span>
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-secondary">Total Brands</span>
              <span className="text-white font-medium">
                {overview?.total_brands || '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-secondary">Growth Outlook</span>
              <span className="text-white font-medium capitalize">
                {overview?.growth_outlook || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Competitive Position */}
        {positionQuery.isLoading ? (
          <LoadingCard />
        ) : position ? (
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Your Position</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="text-3xl font-bold text-primary-400">#{position.rank}</div>
                <div>
                  <div className="text-white font-medium">Market Rank</div>
                  <div className="text-text-secondary text-sm flex items-center gap-1">
                    {(position.total_share * 100).toFixed(1)}% volume share
                    <MetricTooltip metric="shareVolume" position="right" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-text-secondary mb-2">Strengths</div>
                  <ul className="space-y-1">
                    {position.strengths.slice(0, 3).map((s, i) => (
                      <li key={i} className="text-sm text-success-400">• {s}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-sm text-text-secondary mb-2">Opportunities</div>
                  <ul className="space-y-1">
                    {position.opportunities.slice(0, 3).map((o, i) => (
                      <li key={i} className="text-sm text-primary-400">• {o}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Competition Section with Sub-Tabs */}
        <div className="card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Competition</h3>
            <div className="flex items-center gap-1">
              <span className="text-xs text-text-tertiary">Share (Vol)</span>
              <MetricTooltip metric="brandShare" position="left" />
            </div>
          </div>

          {/* Sub-Tab Navigation */}
          <div className="flex border-b border-surface-600 mb-4">
            {[
              { id: 'rankings' as const, label: 'Rankings' },
              { id: 'dynamics' as const, label: 'Dynamics' },
              { id: 'deep-dive' as const, label: 'Player Deep Dive' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setCompetitionTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  competitionTab === tab.id
                    ? 'text-primary-400 border-primary-400'
                    : 'text-text-secondary border-transparent hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Rankings Tab */}
          {competitionTab === 'rankings' && (
            <div>
              {overview?.top_brands && overview.top_brands.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-surface-400/30">
                        <th className="text-left py-2 text-text-secondary font-medium">Rank</th>
                        <th className="text-left py-2 text-text-secondary font-medium">Brand</th>
                        <th className="text-left py-2 text-text-secondary font-medium">Manufacturer</th>
                        <th className="text-right py-2 text-text-secondary font-medium">Share</th>
                        <th className="text-right py-2 text-text-secondary font-medium">Growth</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.top_brands.map((brand, index) => (
                        <tr key={brand.name} className="border-b border-surface-400/20 last:border-0">
                          <td className="py-3 text-text-tertiary">#{index + 1}</td>
                          <td className="py-3 text-white font-medium">{brand.name}</td>
                          <td className="py-3 text-text-secondary">{brand.manufacturer || '-'}</td>
                          <td className="py-3 text-right text-primary-400 font-medium">
                            {(brand.share * 100).toFixed(1)}%
                          </td>
                          <td className={`py-3 text-right ${(brand.growth || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                            {(brand.growth || 0) >= 0 ? '+' : ''}{((brand.growth || 0) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center border border-dashed border-surface-400/30 rounded-lg">
                  <span className="text-text-tertiary">No brand data available</span>
                </div>
              )}
            </div>
          )}

          {/* Dynamics Tab */}
          {competitionTab === 'dynamics' && (
            <div className="space-y-6">
              {/* Share Movement Chart */}
              <div>
                <h4 className="text-sm font-medium text-white mb-3">Share Movement (Last 4 Quarters)</h4>
                <SimpleLineChart
                  data={[
                    { name: 'Q1 2025', 'Coca-Cola': 32.5, 'Pepsi': 18.2, 'Red Cola': 8.5, 'Big Cola': 6.2 },
                    { name: 'Q2 2025', 'Coca-Cola': 32.3, 'Pepsi': 18.4, 'Red Cola': 8.8, 'Big Cola': 6.4 },
                    { name: 'Q3 2025', 'Coca-Cola': 32.0, 'Pepsi': 18.5, 'Red Cola': 9.0, 'Big Cola': 6.5 },
                    { name: 'Q4 2025', 'Coca-Cola': 31.8, 'Pepsi': 18.6, 'Red Cola': 9.2, 'Big Cola': 6.7 },
                  ]}
                  lines={[
                    { dataKey: 'Coca-Cola', name: 'Coca-Cola', color: '#ef4444' },
                    { dataKey: 'Pepsi', name: 'Pepsi', color: '#3b82f6' },
                    { dataKey: 'Red Cola', name: 'Red Cola', color: '#f97316' },
                    { dataKey: 'Big Cola', name: 'Big Cola', color: '#22c55e' },
                  ]}
                  height={250}
                  showGrid={true}
                  showLegend={true}
                />
              </div>

              {/* Gainers and Losers */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-surface-700/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-success-400 mb-3 flex items-center gap-2">
                    <span>↑</span> Share Gainers
                  </h4>
                  <div className="space-y-2">
                    {overview?.top_brands
                      ?.filter(b => (b.growth || 0) > 0)
                      .sort((a, b) => (b.growth || 0) - (a.growth || 0))
                      .slice(0, 5)
                      .map((brand, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-white text-sm">{brand.name}</span>
                          <span className="text-success-400 text-sm">+{((brand.growth || 0) * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                  </div>
                </div>
                <div className="bg-surface-700/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-danger-400 mb-3 flex items-center gap-2">
                    <span>↓</span> Share Losers
                  </h4>
                  <div className="space-y-2">
                    {overview?.top_brands
                      ?.filter(b => (b.growth || 0) < 0)
                      .sort((a, b) => (a.growth || 0) - (b.growth || 0))
                      .slice(0, 5)
                      .map((brand, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-white text-sm">{brand.name}</span>
                          <span className="text-danger-400 text-sm">{((brand.growth || 0) * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    {!overview?.top_brands?.some(b => (b.growth || 0) < 0) && (
                      <span className="text-text-tertiary text-sm">No share losers</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Player Deep Dive Tab */}
          {competitionTab === 'deep-dive' && (
            <div className="space-y-6">
              {/* Competitor Selector */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Select Competitor
                </label>
                <div className="flex flex-wrap gap-2">
                  {overview?.top_brands?.slice(0, 8).map((brand) => (
                    <button
                      key={brand.name}
                      onClick={() => setSelectedCompetitorName(brand.name)}
                      className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                        selectedCompetitorName === brand.name
                          ? 'bg-primary-500 text-white'
                          : 'bg-surface-600 text-text-secondary hover:bg-surface-500 hover:text-white'
                      }`}
                    >
                      {brand.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Deep Dive Content */}
              {selectedCompetitor ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
                    <div>
                      <h4 className="text-lg font-semibold text-white">{selectedCompetitor.name}</h4>
                      <p className="text-sm text-text-tertiary">{selectedCompetitor.manufacturer || 'Unknown Manufacturer'}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-primary-400">
                          #{overview?.top_brands?.findIndex(b => b.name === selectedCompetitorName)! + 1}
                        </div>
                        <div className="text-xs text-text-tertiary">Rank</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white">
                          {(selectedCompetitor.share * 100).toFixed(1)}%
                        </div>
                        <div className="text-xs text-text-tertiary">Share</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-2xl font-bold ${(selectedCompetitor.growth || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                          {(selectedCompetitor.growth || 0) >= 0 ? '+' : ''}{((selectedCompetitor.growth || 0) * 100).toFixed(1)}%
                        </div>
                        <div className="text-xs text-text-tertiary">Growth</div>
                      </div>
                    </div>
                  </div>

                  {/* Top 5 SKUs by Revenue */}
                  <div>
                    <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                      Top 5 SKUs by Revenue
                      <span className="text-xs text-text-tertiary font-normal">
                        (Highest revenue-generating products)
                      </span>
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-surface-400/30">
                            <th className="text-left py-2 text-text-secondary font-medium">#</th>
                            <th className="text-left py-2 text-text-secondary font-medium">Product</th>
                            <th className="text-left py-2 text-text-secondary font-medium">Size</th>
                            <th className="text-right py-2 text-text-secondary font-medium">Revenue</th>
                            <th className="text-right py-2 text-text-secondary font-medium">% of Brand</th>
                            <th className="text-right py-2 text-text-secondary font-medium">Growth</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getCompetitorSKUs(selectedCompetitor.name).map((sku, idx) => (
                            <tr key={sku.gtin} className="border-b border-surface-400/20 last:border-0">
                              <td className="py-3 text-text-tertiary">{idx + 1}</td>
                              <td className="py-3">
                                <div className="text-white font-medium">{sku.name}</div>
                                <div className="text-xs text-text-tertiary font-mono">{sku.gtin}</div>
                              </td>
                              <td className="py-3 text-text-secondary">{sku.size}</td>
                              <td className="py-3 text-right text-white font-medium">
                                {formatCurrency(sku.revenue)}
                              </td>
                              <td className="py-3 text-right text-primary-400">
                                {(sku.revenueShare * 100).toFixed(0)}%
                              </td>
                              <td className={`py-3 text-right ${sku.growth >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                                {sku.growth >= 0 ? '+' : ''}{(sku.growth * 100).toFixed(1)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Quick Insights */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-surface-700/30 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-success-400 mb-3">Key Strengths</h4>
                      <ul className="space-y-2">
                        <li className="text-sm text-text-secondary flex items-start gap-2">
                          <span className="text-success-400">•</span>
                          Strong brand recognition
                        </li>
                        <li className="text-sm text-text-secondary flex items-start gap-2">
                          <span className="text-success-400">•</span>
                          Wide distribution network
                        </li>
                        <li className="text-sm text-text-secondary flex items-start gap-2">
                          <span className="text-success-400">•</span>
                          Competitive pricing
                        </li>
                      </ul>
                    </div>
                    <div className="bg-surface-700/30 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-danger-400 mb-3">Threats to Watch</h4>
                      <ul className="space-y-2">
                        <li className="text-sm text-text-secondary flex items-start gap-2">
                          <span className="text-danger-400">!</span>
                          Aggressive digital growth
                        </li>
                        <li className="text-sm text-text-secondary flex items-start gap-2">
                          <span className="text-danger-400">!</span>
                          New product launches planned
                        </li>
                        <li className="text-sm text-text-secondary flex items-start gap-2">
                          <span className="text-danger-400">!</span>
                          Increased marketing investment
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center border border-dashed border-surface-400/30 rounded-lg">
                  <span className="text-text-tertiary">Select a competitor above to see detailed analysis</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Segment Analysis */}
        <div className="card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Segment Analysis</h3>
            <span className="text-xs text-text-tertiary">Click row to see attribute preferences</span>
          </div>
          {segmentsQuery.isLoading ? (
            <div className="space-y-2">
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
            </div>
          ) : segments?.segments ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-400/30">
                    <th className="text-left py-2 text-text-secondary font-medium">Segment</th>
                    <th className="text-right py-2 text-text-secondary font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <span>Share (Vol)</span>
                        <MetricTooltip metric="segmentShare" position="bottom" />
                      </div>
                    </th>
                    <th className="text-right py-2 text-text-secondary font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <span>Price Sensitivity</span>
                        <MetricTooltip metric="priceSensitivity" position="bottom" />
                      </div>
                    </th>
                    <th className="text-left py-2 text-text-secondary font-medium pl-6">Characteristics</th>
                  </tr>
                </thead>
                <tbody>
                  {segments.segments.map((segment, index) => (
                    <SegmentRow
                      key={segment.id}
                      segment={segment}
                      isLast={index === segments.segments.length - 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-text-secondary">No segment data available</p>
          )}
        </div>

        {/* Channel Analysis */}
        <div className="card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Channel Analysis</h3>
            <span className="text-xs text-text-tertiary">Click row to see subchannels</span>
          </div>
          {channelsQuery.isLoading ? (
            <div className="space-y-2">
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
            </div>
          ) : channelsQuery.data?.channels ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-400/30">
                    <th className="text-left py-2 text-text-secondary font-medium">Channel</th>
                    <th className="text-right py-2 text-text-secondary font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <span>Share (Vol)</span>
                        <MetricTooltip metric="channelShare" position="bottom" />
                      </div>
                    </th>
                    <th className="text-right py-2 text-text-secondary font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <span>Growth (Vol YoY)</span>
                        <MetricTooltip metric="channelGrowth" position="bottom" />
                      </div>
                    </th>
                    <th className="text-left py-2 text-text-secondary font-medium pl-6">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {channelsQuery.data.channels.map((channel, index) => (
                    <ChannelRow
                      key={channel.id}
                      channel={channel}
                      isLast={index === channelsQuery.data!.channels.length - 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-text-secondary">No channel data available</p>
          )}
        </div>
      </div>
    </div>
  );
}
